import { useState } from "react";
import { Controller, useController, useFormContext } from "react-hook-form";
import { FIELD_REQUIRED } from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { getReactHookFormNestedError, LaunchError } from "../../utils/errors";
import _ from "lodash";
import ModalInstantFormsList from "./ModalInstantFormsList";
import ModalViewInstantForm from "./ModalViewInstantForm";
import ModalCreateInstantForm from "./ModalCreateInstantForm";
import InstantFormCardActions from "../LeadForm/InstantFormCardActions";
import MetaInstantFormSummaryCard from "./MetaInstantFormSummaryCard";
import { META } from "../../utils/constants";
import {
  applyCreatedLeadFormFromCard,
  collectOpenDraftFormTargets,
  prepareRecommendedLeadFormCreate,
  readRecommendedLeadForm,
  toSelectedInstantFormValue,
} from "../../utils/leadFormInitialValues";
import { stampRecommendedLeadFormTargets } from "../../utils/aiGeneratedForm";
import { mergeLeadFormPreviewStorageFields } from "../../utils/leadFormPreview";

const InstantFormContainer = ({
  name,
  integrationID,
  pageID,
  control,
  errors = {},
  disabled,
}) => {
  const {
    field: { value, onChange },
  } = useController({
    control,
    name,
    defaultValue: "",
  });
  const [modalTemplateOpen, setModalTemplateOpen] = useState(false);
  const [modalViewOpen, setModalViewOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createInitialValues, setCreateInitialValues] = useState(null);
  const [listCreateInitialValues, setListCreateInitialValues] = useState(null);
  const [recommendedAdIDs, setRecommendedAdIDs] = useState(null);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { watch, sectionSelected, getValues, setValue, campaignID } =
    useFormContext();
  const campaignLanguage = watch("language");
  const recommendations = watch(`platformsData.${META}.recommendations`);
  const advertiserID = useSelectorApp(
    (state) => state.auth?.user?.advertiserID
  );
  const leadFormRecommendation = {
    adID: sectionSelected?.adID,
    adGroupID: sectionSelected?.adGroupID,
    campaignID,
    advertiserID,
    language: campaignLanguage,
    getCampaignValues: getValues,
    setCampaignValue: setValue,
  };
  const isEmptyTemplate = _.isEmpty(value);

  const onPrepareCreate = () =>
    prepareRecommendedLeadFormCreate({
      platform: META,
      recommendations,
      entityID: sectionSelected?.adID,
      adsGroups: getValues("adsGroups"),
      language: campaignLanguage,
      dispatch,
      launchError: LaunchError,
    });

  const onToggleModalTemplate = () => {
    if (disabled) return;
    if (!modalTemplateOpen) {
      setListCreateInitialValues(onPrepareCreate().initialValues);
    }
    setModalTemplateOpen((prev) => !prev);
  };

  const onApplyInstantForm = ({
    instantForm,
    fallbackCreationTime = false,
  }) => {
    const instantFormValue = toSelectedInstantFormValue({
      platform: META,
      form: instantForm,
      fallbackCreationTime,
    });
    if (!instantFormValue) return;
    onChange(instantFormValue);
  };

  const recommendedLeadForm = readRecommendedLeadForm({
    platform: META,
    recommendations,
    entityID: sectionSelected?.adID,
    adsGroups: getValues("adsGroups"),
  });

  const onOpenCreateModal = () => {
    if (disabled) return;
    const prepared = onPrepareCreate();
    setCreateInitialValues(prepared.initialValues);
    setRecommendedAdIDs(prepared.adIDs);
    setCreateModalOpen(true);
  };

  const onCloseCreateModal = () => {
    setCreateModalOpen(false);
    setCreateInitialValues(null);
    setRecommendedAdIDs(null);
  };

  const onSaveCreatedInstantForm = (instantForm) => {
    let instantFormValue = toSelectedInstantFormValue({
      platform: META,
      form: instantForm,
      fallbackCreationTime: true,
    });
    if (!instantFormValue) return;
    // The editor may have generated and stored a recommendation after it
    // opened with defaults; it is assigned like one prepared on open.
    const prepared = createInitialValues
      ? { initialValues: createInitialValues, adIDs: recommendedAdIDs }
      : onPrepareCreate();
    const stampTargets = prepared.initialValues
      ? collectOpenDraftFormTargets({
          platform: META,
          adsGroups: getValues?.("adsGroups"),
          assetsGroups: getValues?.("assetsGroups"),
          adIDs: prepared.adIDs,
          currentAdID: sectionSelected?.adID,
        })
      : [];
    if (prepared.initialValues) {
      instantFormValue = {
        ...instantFormValue,
        ...mergeLeadFormPreviewStorageFields(
          META,
          instantForm,
          prepared.initialValues
        ),
      };
    }
    applyCreatedLeadFormFromCard({
      platform: META,
      formValue: instantFormValue,
      hasRecommendation: Boolean(prepared.initialValues),
      adIDs: prepared.adIDs,
      currentAdID: sectionSelected?.adID,
      getValues,
      setValue,
      onChange,
    });
    if (stampTargets.length > 0) {
      stampRecommendedLeadFormTargets({
        getValues,
        setValue,
        targets: stampTargets,
      });
    }
  };

  const showActions = isEmptyTemplate || !disabled;

  return (
    <Controller
      name={name}
      id={name}
      control={control}
      rules={{
        required: {
          value: true,
          message: t(FIELD_REQUIRED),
        },
      }}
      render={() => (
        <Box id={name}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {!isEmptyTemplate && (
              <Box
                sx={{
                  width: "100%",
                }}
              >
                <MetaInstantFormSummaryCard
                  form={value}
                  recommendation={recommendedLeadForm}
                  integrationID={integrationID}
                  pageID={pageID}
                  onView={() => setModalViewOpen(true)}
                />
              </Box>
            )}
            {showActions && (
              <InstantFormCardActions
                disabled={disabled}
                hasSelection={!isEmptyTemplate}
                onSelect={onToggleModalTemplate}
                onCreate={onOpenCreateModal}
                marginTop={1.5}
              />
            )}
          </Box>
          {error?.message && (
            <Typography variant="body2" color="error">
              {error.message}
            </Typography>
          )}
          {modalTemplateOpen && (
            <ModalInstantFormsList
              modalOpen={modalTemplateOpen}
              onCloseModal={onToggleModalTemplate}
              integrationID={integrationID}
              pageID={pageID}
              createInitialValues={listCreateInitialValues}
              leadFormRecommendation={leadFormRecommendation}
              onCallbackSelect={(instantForm) =>
                onApplyInstantForm({ instantForm })
              }
            />
          )}
          {createModalOpen && (
            <ModalCreateInstantForm
              modalOpen={createModalOpen}
              onCloseModal={onCloseCreateModal}
              integrationID={integrationID}
              pageID={pageID}
              campaignLanguage={campaignLanguage}
              initialValues={createInitialValues}
              leadFormRecommendation={leadFormRecommendation}
              aiGeneratedDraft={Boolean(createInitialValues)}
              onCallbackSave={onSaveCreatedInstantForm}
            />
          )}
          {modalViewOpen && (
            <ModalViewInstantForm
              modalOpen
              onCloseModal={() => setModalViewOpen(false)}
              onCallbackSave={(instantForm) =>
                onApplyInstantForm({
                  instantForm,
                  fallbackCreationTime: true,
                })
              }
              instantForm={value}
              integrationID={integrationID}
              pageID={pageID}
            />
          )}
        </Box>
      )}
    />
  );
};

export default InstantFormContainer;
